import { useIsFocused, useNavigation } from '@react-navigation/native'
import { useFlagsStatus } from '@unleash/proxy-client-react'
import { useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Feature, useFeatureFlag } from '@src/components'
import { userSelector } from '@src/selectors/app'
import { policyConsentStatusesSelector } from '@src/selectors/policyConsents'
import { AllSurveyLinksQuery } from '@src/graphql/fetchQuestionnaire.generated'
import { Storage, useDispatchAsync, User } from '@src/utils'
import {
  EhrAppointmentStateKind,
  LegalPolicyConsentKind,
  LegalPolicyConsentState,
  SurveysConfigKind,
  UiStateNames,
} from '@src/types'
import { sensorSelector } from '@src/selectors/sensor'
import { useGetRequiredPolicyConsents } from '@src/screens/PolicyConsents/hooks/useGetRequiredPolicyConsents'
import { useHasMedicalSubscription } from '@src/hooks/useHasMedicalSubscription'
import { useActiveSubscriptions } from '@src/hooks/useActiveSubscriptions'
import { useVideoCallProductType } from '@src/hooks/useVideoCallProductType.ts'
import { StartSurveyMutation } from '@src/graphql/startQuestionnaire.generated'
import { uiFlagsSelector } from '@src/selectors/uiFlags'
import { useUhcSubscriptionExpiredModal } from '@screens/Uhc/hooks/useUhcSubscriptionExpiredModal.ts'
import { allEhrAppointmentsSelector } from '@src/screens/NutritionistHub/models/nutritionistHub.selectors'
import { useLazyScreenPreload } from '@navigation/utils.tsx'
import { useGetByosVideoCallUpsellNextStep } from '../useGetByosVideoCallUpsellNextStep'
import { ByosVideoCallsUpsellStep } from '../../constants'
import { getPermissionsModalStateInteractions } from '../../utils'
import {
  getStoreReviewPromptStateInteractions,
  getVideoCallsUpsellRouteParams,
  shouldShowAppRatingModal,
  shouldShowCgmMedicalDisclaimerSurvey,
  shouldShowInsuranceQuestionnaire,
  shouldShowMedicalDisclaimerSurvey,
  shouldShowMentalHealthServicesInterestSurvey,
  shouldShowRecurringSubscriptionConsentSurvey,
  shouldShowVideoCallsSteloUpsellBanner,
  shouldShowWeightLossExperienceOnboarding,
} from './utils'
import { useShouldShowOwnSensorActivatedVideoCallUpsellModal } from './hooks'

export const useOnboardingModals = () => {
  /**
   * Lib Hooks
   */
  const markedNotificationPermissionsShown = useRef(false)
  const navigation = useNavigation()
  const dispatch = useDispatch()
  const dispatchAsync = useDispatchAsync()
  const isFocused = useIsFocused()

  /**
   * Selectors
   */
  const user = useSelector(userSelector)
  const sensor = useSelector(sensorSelector)
  const uiFlags = useSelector(uiFlagsSelector)
  const allEhrAppointments = useSelector(allEhrAppointmentsSelector)

  /**
   * Feature Flags
   */
  const medicalDisclaimerSurveyEnabled = useFeatureFlag(Feature.MedicalDisclaimerSurvey)
  const videoCallsSteloUpsellFeatureFlagEnabled = useFeatureFlag(Feature.VideoCallsSteloUpsell)
  const mentalHealthServicesInterestSurveyEnabled = useFeatureFlag(
    Feature.MentalHealthServicesInterestSurvey,
  )
  const newRatingsEnabled = useFeatureFlag(Feature.NewRatings)
  const fullscriptLabsEnabled = useFeatureFlag(Feature.FullscriptLabs)

  /**
   * Shown Hooks
   */
  const shouldShowOwnSensorActivatedVideoCallUpsellModal = useShouldShowOwnSensorActivatedVideoCallUpsellModal()
  const { showModal: shouldShowUhcSubscriptionExpiredModal } = useUhcSubscriptionExpiredModal()

  /**
   * Other Hooks
   */
  const { flagsReady, flagsError } = useFlagsStatus()
  const flagsLoaded = flagsReady || flagsError
  const { getRequiredPolicyConsents } = useGetRequiredPolicyConsents()
  const policyConsentStatuses = useSelector(policyConsentStatusesSelector)
  const policyConsentStatusesLoaded = policyConsentStatuses.length > 0

  const medicalDisclaimerConsentAccepted = policyConsentStatusesLoaded
    ? policyConsentStatuses.some(
        (status) =>
          status.kind === LegalPolicyConsentKind.MedicalDisclaimer &&
          status.lastPolicyConsent?.state === LegalPolicyConsentState.Accepted,
      )
    : undefined

  const recurringSubscriptionConsentAccepted = policyConsentStatusesLoaded
    ? policyConsentStatuses.some(
        (status) =>
          status.kind === LegalPolicyConsentKind.RecurringSubscription &&
          status.lastPolicyConsent?.state === LegalPolicyConsentState.Accepted,
      )
    : undefined

  const byosVideoCallsUpsellNextStep = useGetByosVideoCallUpsellNextStep()
  const hasMedicalSubscription = useHasMedicalSubscription()
  const activeSubscriptions = useActiveSubscriptions()
  const { isInsuranceVideoCallProductType } = useVideoCallProductType()

  /**
   * Constants
   */
  const subscriptionIncludesCgm = activeSubscriptions.some(
    (subscription) => subscription.primaryProduct.includesCgm,
  )
  const lastCoreSubscription = user?.lastCoreSubscription
  const hasMonthlyCoreSubscription = activeSubscriptions.some(
    (subscription) =>
      subscription.primaryProduct.core && subscription.primaryProduct.interval.key === 'monthly',
  )
  const hasNonCanceledMonthlyCoreSubscription = activeSubscriptions.some(
    (subscription) =>
      subscription.primaryProduct.core &&
      subscription.primaryProduct.interval.key === 'monthly' &&
      !subscription.cancelAt,
  )
  const hasBundledPaidCallsSubscription = activeSubscriptions.some(
    (subscription) => subscription.primaryProduct.bundledPaidCalls,
  )
  const userId = user?.id
  const scheduledFirstAppointment = allEhrAppointments.some((appointment) =>
    [EhrAppointmentStateKind.Scheduled, EhrAppointmentStateKind.InReview].includes(
      appointment.lastState.kind,
    ),
  )

  const {
    showVideoCallSteloUpsellModal,
    showReferredToPurchaseModal,
    showChurnFeedbackModal,
    showProductMarketFitSurvey,
    showNetPromoterScoreSurvey,
    rateAppEligible,
    showReferralPopup,
    showFullscriptLabsUpsellModal,
    showFreeCallTrialUpsellPopup,
    showOnboardingInsuranceSurvey = false,
    showAddInsuranceUpsells = false,
    insuranceInformationSubmitted = false,
  } = uiFlags

  const videoCallsSteloUpsellEnabled =
    (videoCallsSteloUpsellFeatureFlagEnabled && showVideoCallSteloUpsellModal) || false

  const shouldShowLabPanelsUpsellModal = fullscriptLabsEnabled && showFullscriptLabsUpsellModal

  useLazyScreenPreload('LabPanelsUpsellModal', {
    shouldPreload: shouldShowLabPanelsUpsellModal,
  })

  useLazyScreenPreload('FreeCallTrialUpsellPopup', {
    shouldPreload: showFreeCallTrialUpsellPopup,
  })

  /**
   * Interactions
   */
  const {
    shouldShow: shouldShowStoreReviewPrompt,
    markAsShown: markStoreReviewPromptAsShown,
  } = useMemo(() => getStoreReviewPromptStateInteractions({ sensor }), [sensor])
  const { shouldShow: shouldShowPermissionsModal } = useMemo(
    () => getPermissionsModalStateInteractions(),
    [],
  )
  useEffect(() => {
    const handleModals = async () => {
      if (!flagsLoaded || !isFocused) {
        return
      }

      // using the useImpersonationContext leads to a race condition that shows the welcome tour during impersonation
      const isImpersonating = User.isImpersonating()

      if (isImpersonating) {
        return
      }

      const getRoutes = async () => {
        const routes: { name: string; params?: any }[] = []

        let surveyLinks: AllSurveyLinksQuery['allSurveyLinks']['surveyLinks'] = []

        try {
          const surveyLinksResponse = await dispatchAsync<AllSurveyLinksQuery['allSurveyLinks']>({
            type: 'app/surveyQuestionnaire',
          })
          surveyLinks = surveyLinksResponse.surveyLinks
        } catch {
          // do nothing
        }

        if (showProductMarketFitSurvey) {
          try {
            const pendingProductMarketFitSurveyLink = surveyLinks.find(
              (link) => link.survey.kind === SurveysConfigKind.ProductMarketFit && !link.finished,
            )
            let questionsCount: number
            if (!pendingProductMarketFitSurveyLink) {
              const newProductMarketFitSurveyLink = await dispatchAsync<
                StartSurveyMutation['startSurvey']
              >({
                type: 'app/startQuestionnaire',
                payload: { kind: SurveysConfigKind.ProductMarketFit },
              })
              questionsCount = newProductMarketFitSurveyLink.config.questionsCount
            } else {
              questionsCount = pendingProductMarketFitSurveyLink.config.questionsCount
            }

            dispatch({
              type: 'uiFlags/updateUiFlags',
              payload: { showProductMarketFitSurvey: false },
            })
            dispatch({
              type: 'app/upsertUiState',
              payload: {
                name: UiStateNames.ProductMarketFitSurveyVisited,
                value: true,
              },
              complete: () => {
                dispatch({ type: 'uiFlags/fetch' })
              },
            })

            routes.push({
              name: 'GiveFeedbackModal',
              params: {
                questionnaire: SurveysConfigKind.ProductMarketFit,
                questionsCount,
                durationInMinutes: 2,
              },
            })
            return routes
          } catch {
            // do nothing
          }
        }

        if (showNetPromoterScoreSurvey) {
          try {
            const pendingNetPromoterScoreSurveyLink = surveyLinks.find(
              (link) => link.survey.kind === SurveysConfigKind.NetPromoterScore && !link.finished,
            )
            let questionsCount: number
            if (!pendingNetPromoterScoreSurveyLink) {
              const newNetPromoterScoreSurveyLink = await dispatchAsync<
                StartSurveyMutation['startSurvey']
              >({
                type: 'app/startQuestionnaire',
                payload: { kind: SurveysConfigKind.NetPromoterScore },
              })
              questionsCount = newNetPromoterScoreSurveyLink.config.questionsCount
            } else {
              questionsCount = pendingNetPromoterScoreSurveyLink.config.questionsCount
            }

            dispatch({
              type: 'uiFlags/updateUiFlags',
              payload: { showNetPromoterScoreSurvey: false },
            })
            dispatch({
              type: 'app/upsertUiState',
              payload: {
                name: UiStateNames.NetPromoterScoreSurveyVisited,
                value: true,
              },
              complete: () => {
                dispatch({ type: 'uiFlags/fetch' })
              },
            })

            routes.push({
              name: 'GiveFeedbackModal',
              params: {
                questionnaire: SurveysConfigKind.NetPromoterScore,
                questionsCount,
                durationInMinutes: 2,
              },
            })
            return routes
          } catch {
            // do nothing
          }
        }

        if (
          shouldShowRecurringSubscriptionConsentSurvey({
            surveyLinks,
            hasNonCanceledMonthlyCoreSubscription,
            recurringSubscriptionConsentAccepted,
          })
        ) {
          routes.push({
            name: 'Questionnaire',
            params: { questionnaire: SurveysConfigKind.RecurringSubscriptionConsent },
          })
          return routes
        }

        if (
          shouldShowCgmMedicalDisclaimerSurvey({
            surveyLinks,
            subscriptionIncludesCgm,
            medicalDisclaimerConsentAccepted,
          })
        ) {
          routes.push({
            name: 'Questionnaire',
            params: { questionnaire: SurveysConfigKind.CgmMedicalDisclaimer },
          })
          return routes
        }

        if (
          shouldShowMedicalDisclaimerSurvey({
            surveyLinks,
            medicalDisclaimerSurveyEnabled,
            hasMedicalSubscription,
          })
        ) {
          routes.push({
            name: 'Questionnaire',
            params: { questionnaire: SurveysConfigKind.MedicalDisclaimer },
          })
          return routes
        }

        const insuranceUpsellRouteParams = getVideoCallsUpsellRouteParams({
          userId,
          lastCoreSubscription,
          hasBundledPaidCallsSubscription,
          isInsuranceVideoCallProductType,
          showAddInsuranceUpsells,
          showOnboardingInsuranceSurvey,
        })

        if (insuranceUpsellRouteParams) {
          routes.push(insuranceUpsellRouteParams)
          const visitedCountKey = `${Storage.VIDEO_CALLS_UPSELL_MODAL_VISITED_COUNT_KEY}_${userId}`
          Storage.set(visitedCountKey, 1)
        }

        if (
          shouldShowWeightLossExperienceOnboarding({
            userId,
            onboardingExperience: user?.onboardingExperience,
            surveyLinks,
            scheduledFirstAppointment,
          })
        ) {
          routes.push({
            name: 'Questionnaire',
            params: { questionnaire: SurveysConfigKind.OnboardingExperienceBasicInformation },
          })
          return routes
        }

        if (videoCallsSteloUpsellEnabled && shouldShowVideoCallsSteloUpsellBanner()) {
          routes.push({
            name: 'VideoCallsSteloUpsell',
          })
          return routes
        }

        if (showReferredToPurchaseModal) {
          routes.push({
            name: 'ReferredToPurchasedByNutritionistModal',
          })
          return routes
        }

        if (showChurnFeedbackModal) {
          routes.push({
            name: 'ChurnFeedbackModal',
          })
          return routes
        }

        if (
          shouldShowMentalHealthServicesInterestSurvey({
            mentalHealthServicesInterestSurveyEnabled,
            userId,
            surveyLinks,
          })
        ) {
          routes.push({
            name: 'Questionnaire',
            params: { questionnaire: SurveysConfigKind.MentalHealthServicesInterest },
          })
          return routes
        }

        if (showReferralPopup) {
          routes.push({ name: 'ReferralPopup' })
          dispatch({
            type: 'uiFlags/updateUiFlags',
            payload: { showReferralPopup: false },
          })
          return routes
        }

        if (showFreeCallTrialUpsellPopup) {
          routes.push({ name: 'FreeCallTrialUpsellPopup' })
          dispatch({
            type: 'uiFlags/updateUiFlags',
            payload: { showFreeCallTrialUpsellPopup: false },
          })
          return routes
        }

        if (shouldShowLabPanelsUpsellModal) {
          routes.push({ name: 'LabPanelsUpsellModal' })
          dispatch({
            type: 'app/upsertUiState',
            payload: {
              name: UiStateNames.FullscriptLabsUpsellModalShown,
              value: true,
            },
          })
          dispatch({
            type: 'uiFlags/updateUiFlags',
            payload: { showFullscriptLabsUpsellModal: false },
          })
          return routes
        }

        const showStoreReviewPrompt = !newRatingsEnabled && shouldShowStoreReviewPrompt()

        if (showStoreReviewPrompt) {
          routes.push({ name: 'RateOurAppModal' })
          markStoreReviewPromptAsShown()
        }

        const showRateAppModal = await shouldShowAppRatingModal(newRatingsEnabled, rateAppEligible)

        if (showRateAppModal) {
          if (Storage.get<number>(`${Storage.LAST_COACH_RATING_KEY}_${userId}`) === 5) {
            routes.push({ name: 'PositiveCoachRatingRateAppModal' })
          } else {
            routes.push({ name: 'RateAppModal' })
          }
          dispatch({
            type: 'uiFlags/updateUiFlags',
            payload: { rateAppEligible: false },
          })
        }

        if (byosVideoCallsUpsellNextStep === ByosVideoCallsUpsellStep.InitialModal) {
          routes.push({ name: 'NutritionGuidanceUpsellModal' })
        }

        if (byosVideoCallsUpsellNextStep === ByosVideoCallsUpsellStep.FreeConsultationModal) {
          routes.push({ name: 'FreeNutritionistConsultationModal' })
        }

        if (shouldShowOwnSensorActivatedVideoCallUpsellModal) {
          routes.push({ name: 'OwnSensorActivatedNutritionistUpsellModal' })
        }

        const showPermissionsModal = shouldShowPermissionsModal()

        if (showPermissionsModal && !markedNotificationPermissionsShown.current) {
          // preventing a race condition where the modal is shown twice
          markedNotificationPermissionsShown.current = true
          routes.push({ name: 'PermissionsCarousel' })
        }

        // Routes will be pushed in reverse order and we always want the consent modals to show first if present.
        // So, this should be the last item added to the stack.
        //
        const requiredPolicies = await getRequiredPolicyConsents()
        if (requiredPolicies.length > 0) {
          routes.push({ name: 'RequiredPolicyConsent' })
        }

        if (shouldShowUhcSubscriptionExpiredModal) {
          routes.push({ name: 'UhcSubscriptionExpiredModal' })
        }

        if (routes.length === 0) {
          // show insurance questionnaire only if there is nothing else to show
          if (
            userId &&
            showOnboardingInsuranceSurvey &&
            shouldShowInsuranceQuestionnaire({
              hasMonthlyCoreSubscription,
              subscriptionIncludesCgm,
              userId,
              isInsuranceVideoCallProductType,
              hasBundledPaidCallsSubscription,
              showOnboardingInsuranceSurvey,
              insuranceInformationSubmitted,
            })
          ) {
            const insuranceQuestionnaireVisitedKey = `${Storage.INSURANCE_QUESTIONNAIRE_VISITED_KEY}_${userId}`
            Storage.set(insuranceQuestionnaireVisitedKey, true)
            routes.push({ name: 'InsuranceQuestionnaire' })
            return routes
          }
        }

        return routes
      }

      const routes = await getRoutes()

      routes.forEach(({ name, params }) => {
        navigation.navigate(name as any, params)
      })
    }

    handleModals()
  }, [
    byosVideoCallsUpsellNextStep,
    dispatch,
    dispatchAsync,
    flagsLoaded,
    getRequiredPolicyConsents,
    hasBundledPaidCallsSubscription,
    hasMedicalSubscription,
    hasMonthlyCoreSubscription,
    hasNonCanceledMonthlyCoreSubscription,
    insuranceInformationSubmitted,
    isFocused,
    isInsuranceVideoCallProductType,
    lastCoreSubscription,
    markStoreReviewPromptAsShown,
    medicalDisclaimerConsentAccepted,
    medicalDisclaimerSurveyEnabled,
    mentalHealthServicesInterestSurveyEnabled,
    navigation,
    newRatingsEnabled,
    rateAppEligible,
    recurringSubscriptionConsentAccepted,
    scheduledFirstAppointment,
    shouldShowLabPanelsUpsellModal,
    shouldShowOwnSensorActivatedVideoCallUpsellModal,
    shouldShowPermissionsModal,
    shouldShowStoreReviewPrompt,
    shouldShowUhcSubscriptionExpiredModal,
    showAddInsuranceUpsells,
    showChurnFeedbackModal,
    showFreeCallTrialUpsellPopup,
    showNetPromoterScoreSurvey,
    showOnboardingInsuranceSurvey,
    showProductMarketFitSurvey,
    showReferralPopup,
    showReferredToPurchaseModal,
    subscriptionIncludesCgm,
    user?.onboardingExperience,
    userId,
    videoCallsSteloUpsellEnabled,
  ])
}
